import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import type { Transaction } from "@/hooks/useTransactions";
import { parseDateLocal } from "@/utils/dateUtils";
import { formatCurrency, formatNetAmount } from "@/utils/formatters";
import { getTicketHref } from "@/domains/transactions/utils/transactionPayloads";
import { EntityCard } from "@/components/ui/EntityCard";
import { TransactionDataTable } from "./TransactionDataTable";

export interface TransactionActivityItem {
  transaction: Transaction;
  accountName: string;
  categoryName?: string;
  tagName?: string;
  savingPlanName?: string;
  savingPocketLabel?: string;
  installmentName?: string;
  direction: "income" | "expense" | "transfer";
}

export interface TransactionActivityGroup {
  key: string;
  label: string;
  total: number;
  items: TransactionActivityItem[];
}

interface TransactionActivityListProps {
  filteredCount: number;
  groups: TransactionActivityGroup[];
  highlightedTransactionId: number | null;
  selectedTransactionIds: number[];
  isSelectionMode: boolean;
  swipedTransactionId: number | null;
  isDuplicating: boolean;
  visibleCount: number;
  onTransactionClick: (transaction: Transaction) => void;
  onPressStart: (event: ReactPointerEvent<HTMLButtonElement>, transactionId: number) => void;
  onPressMove: (event: ReactPointerEvent<HTMLButtonElement>, transactionId: number) => void;
  onPressEnd: () => void;
  onActionToggle: (event: ReactMouseEvent<HTMLSpanElement>, transactionId: number) => void;
  onDuplicate: (transaction: Transaction) => Promise<void>;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transactionId: number) => Promise<void>;
  onCloseActions: () => void;
  onViewMore: () => void;
}

export const TransactionActivityList = ({
  filteredCount,
  groups,
  highlightedTransactionId,
  selectedTransactionIds,
  isSelectionMode,
  swipedTransactionId,
  isDuplicating,
  visibleCount,
  onTransactionClick,
  onPressStart,
  onPressMove,
  onPressEnd,
  onActionToggle,
  onDuplicate,
  onEdit,
  onDelete,
  onCloseActions,
  onViewMore,
}: TransactionActivityListProps) => {
  const { t } = useTranslation(["common", "transactions"]);

  if (filteredCount === 0) {
    return (
      <p className="text-sm text-[var(--app-text-secondary)]">
        {t("transactions.emptyStateBody", { defaultValue: "No encontramos movimientos con esos filtros." })}
      </p>
    );
  }

  return (
    <>
      <TransactionDataTable
        groups={groups}
        filteredCount={filteredCount}
        visibleCount={visibleCount}
        selectedTransactionIds={selectedTransactionIds}
        isSelectionMode={isSelectionMode}
        isDuplicating={isDuplicating}
        onTransactionClick={onTransactionClick}
        onDuplicate={onDuplicate}
        onEdit={onEdit}
        onDelete={onDelete}
        onViewMore={onViewMore}
      />
      <div className="min-[1024px]:hidden">
        <div className="space-y-6">
          {groups.map((group) => {
            if (group.items.length === 0) return null;

            return (
              <section key={group.key}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-xs font-normal tracking-[0.02em] text-[var(--app-text-secondary)]">
                    {group.label}
                  </h3>
                  <span className="text-xs tabular-nums text-[var(--app-text-secondary)]">
                    {formatNetAmount(group.total)}
                  </span>
                </div>
                <div className="divide-y divide-[color:color-mix(in_srgb,var(--app-border)_80%,transparent)]">
                  {group.items.map((item) => {
                  const isHighlighted = highlightedTransactionId === item.transaction.id;
                  const isSelected = selectedTransactionIds.includes(item.transaction.id);
                  const txDate = parseDateLocal(item.transaction.transaction_date);
                  const typeLabel = item.direction === "expense"
                    ? t("transactions.activityTypeOutflow")
                    : item.direction === "income"
                      ? t("transactions.activityTypeInflow")
                      : t("transactions.activityTypeTransfer");
                  const ticketHref = getTicketHref(item.transaction.ticket_img);

                  return (
                    <div key={item.transaction.id} className="py-4 first:pt-0 last:pb-0">
                      <EntityCard
                        title={item.transaction.name}
                        subtitle={`${typeLabel} • ${txDate.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`}
                        description={item.transaction.description?.trim() || undefined}
                        value={
                          <span className={isHighlighted ? "text-[1.05rem]" : undefined}>
                            {item.direction === "expense" ? "-" : item.direction === "income" ? "+" : ""}
                            {formatCurrency(item.transaction.amount)}
                          </span>
                        }
                        valueTone={item.direction === "expense" ? "negative" : item.direction === "income" ? "positive" : "warning"}
                        meta={[
                          <span>{t("common.labels.account")}: {item.accountName}</span>,
                          item.categoryName ? <span>{t("common.labels.category")}: {item.categoryName}</span> : null,
                          item.tagName ? <span>{t("common.labels.tag")}: {item.tagName}</span> : null,
                          item.savingPlanName ? <span>{t("common.createLabels.savingsGoal")}: {item.savingPlanName}</span> : null,
                          FEATURE_FLAGS.savingPockets && item.savingPocketLabel ? <span>{t("common.createLabels.savingPockets")}: {item.savingPocketLabel}</span> : null,
                          item.installmentName ? <span>{t("common.labels.installment")}: {item.installmentName}</span> : null,
                          ticketHref ? <a href={ticketHref} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="text-[var(--app-text)] underline decoration-[color:color-mix(in_srgb,var(--app-text-secondary)_45%,transparent)] underline-offset-2">{t("transactions.ticket")}</a> : null,
                        ]}
                        actions={[
                          {
                            kind: "duplicate",
                            label: `${t("common:actions.duplicate", { defaultValue: "Duplicate" })} ${item.transaction.name}`,
                            onClick: () => void onDuplicate(item.transaction),
                            disabled: isDuplicating,
                          },
                          {
                            kind: "edit",
                            label: `${t("common:actions.edit")} ${item.transaction.name}`,
                            onClick: () => onEdit(item.transaction),
                          },
                          {
                            kind: "delete",
                            label: `${t("common:actions.delete")} ${item.transaction.name}`,
                            onClick: () => void onDelete(item.transaction.id).then(onCloseActions),
                          },
                        ]}
                        selection={{ active: isSelectionMode, selected: isSelected }}
                        isSwiped={swipedTransactionId === item.transaction.id}
                        toggleLabel={`${swipedTransactionId === item.transaction.id ? t("common.actions.close", { defaultValue: "Close" }) : t("common.actions.open", { defaultValue: "Open" })} ${t("common.actions.edit").toLowerCase()}, ${t("common.actions.duplicate", { defaultValue: "duplicate" }).toLowerCase()} ${t("common.actions.delete").toLowerCase()}`}
                        onClick={() => onTransactionClick(item.transaction)}
                        onPointerDown={(event) => onPressStart(event, item.transaction.id)}
                        onPointerMove={(event) => onPressMove(event, item.transaction.id)}
                        onPointerUp={onPressEnd}
                        onPointerLeave={onPressEnd}
                        onPointerCancel={onPressEnd}
                        onToggle={(event) => onActionToggle(event, item.transaction.id)}
                      />
                    </div>
                  );
                  })}
                </div>
              </section>
            );
          })}
        </div>
        {filteredCount > visibleCount ? (
          <div className="mt-3 flex justify-end pb-8">
            <button type="button" onClick={onViewMore} className="app-focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium text-[var(--app-text)] underline underline-offset-4 hover:bg-[var(--app-row-hover-bg)]">
              {t("transactions.viewMore", { defaultValue: "Ver más movimientos" })} <span aria-hidden="true" className="ml-1">›</span>
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
};
